"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"

import GraphPanel from "./GraphPanel"
import SchemaHeader from "./SchemaHeader"
import SchemaToolbar from "./SchemaToolbar"
import SchemaEditor from "./SchemaEditor"
import InfoSection from "./InfoSection"
import SavedSchemasList from "./SavedSchemasList"
import ValidationToast from "./ValidationToast"
import GraphElementModal from "./GraphElementModal"
import { PGS_LANGUAGE_ID } from "./pgs-language"

import { parseGraphFromCode } from "./graph-utils"
import {
  monarch,
  validatePGS,
  completionItems,
  provideCodeActions,
} from "./pgs-language"
import {
  loadSavedSchemas,
  saveSavedSchemas,
  upsertSchema,
  dl,
} from "./schema-storage"

function normalizeLabels(raw) {
  return (raw || "")
    .split(",")
    .map((l) => l.trim())
    .filter(Boolean)
}

export default function CreateSchema() {
  const [code, setCode] = useState("")
  const [schemaName, setSchemaName] = useState("")
  const [validToast, setValidToast] = useState(null)
  const editorRef = useRef(null)
  const [monacoRef, setMonacoRef] = useState(null)

  const [savedSchemas, setSavedSchemas] = useState([])
  const [editingId, setEditingId] = useState(null)

  const [graphEditMode, setGraphEditMode] = useState(null) // "add-edge" | null
  const [edgeSourceId, setEdgeSourceId] = useState(null)

  const [elementModalOpen, setElementModalOpen] = useState(false)
  const [elementModalMode, setElementModalMode] = useState(null) // "node" | "edge" | null
  const [edgeContext, setEdgeContext] = useState(null) // { src, dst }

  useEffect(() => {
    setSavedSchemas(loadSavedSchemas())
  }, [])

  const editorTheme = useMemo(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark")
        ? "vs-dark"
        : "vs",
    []
  )

  const graphData = useMemo(() => parseGraphFromCode(code), [code])

  const appendSnippetToEditor = (snippet) => {
    const model = editorRef.current?.getModel?.()
    if (model) {
      const prev = model.getValue()
      const base = prev === "" || prev.endsWith("\n") ? prev : prev + "\n"
      const next = base + snippet + "\n"
      model.setValue(next)
      setCode(next)
    } else {
      setCode((prev) => {
        const base = prev === "" || prev.endsWith("\n") ? prev : prev + "\n"
        return base + snippet + "\n"
      })
    }
  }

    const handleEditorWillMount = (monaco) => {
    setMonacoRef(monaco)
    if (!monaco.languages.getLanguages().some((l) => l.id === "pgs")) {
        monaco.languages.register({ id: PGS_LANGUAGE_ID })
        monaco.languages.setMonarchTokensProvider(PGS_LANGUAGE_ID, monarch)

        monaco.languages.registerCompletionItemProvider(PGS_LANGUAGE_ID, {
        triggerCharacters: [" ", "[", "(", "{", ":", "c", "C"],
        provideCompletionItems: (model, position) => {
            return {
            suggestions: completionItems.map((c) => ({
                ...c,
                range: undefined,
            })),
            }
        },
        })

        monaco.languages.registerCodeActionProvider(PGS_LANGUAGE_ID, {
        provideCodeActions(model, range) {
            return provideCodeActions(model, range)
        },
        providedCodeActionKinds: ["quickfix"],
        })

        monaco.languages.setLanguageConfiguration(PGS_LANGUAGE_ID, {
        autoClosingPairs: [
            { open: "(", close: ")" },
            { open: "[", close: "]" },
            { open: "{", close: "}" },
        ],
        surroundingPairs: [
            { open: "(", close: ")" },
            { open: "[", close: "]" },
            { open: "{", close: "}" },
        ],
        brackets: [
            ["{", "}"],
            ["[", "]"],
            ["(", ")"],
        ],
        })
    }
    }


  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor

    const runValidate = () => {
      const text = editor.getModel().getValue()
      const markers = validatePGS(text)
      monaco.editor.setModelMarkers(editor.getModel(), "pgs-diagnostics", markers)
    }

    runValidate()
    const sub = editor.onDidChangeModelContent(runValidate)

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      const markers = validatePGS(editor.getModel().getValue())
      if (markers.length === 0) {
        setValidToast({ ok: true, msg: "Schema validated successfully!" })
      } else {
        setValidToast({ ok: false, msg: "Schema has issues. Check markers." })
      }
      setTimeout(() => setValidToast(null), 3000)
    })

    editor.onDidDispose(() => sub && sub.dispose())
  }

    const handleValidateClick = () => {
    if (!editorRef.current || !monacoRef) return
    const text = editorRef.current.getModel().getValue()
    const markers = validatePGS(text)
    monacoRef.editor.setModelMarkers(
        editorRef.current.getModel(),
        "pgs-diagnostics",
        markers
    )
    setValidToast(
        markers.length === 0
        ? { ok: true, msg: "Schema validated successfully!" }
        : { ok: false, msg: "Schema has issues. Check markers." }
    )
    setTimeout(() => setValidToast(null), 3000)
    }


  const onSaveSchema = () => {
    const name = schemaName.trim() || "Untitled Schema"
    const currentCode = editorRef.current?.getModel()?.getValue() || code || ""
    const next = upsertSchema(savedSchemas, { id: editingId, name, code: currentCode })
    saveSavedSchemas(next)
    setSavedSchemas(next)
    setValidToast({ ok: true, msg: editingId ? "Schema updated." : "Schema saved." })
    setTimeout(() => setValidToast(null), 2000)
  }

  const onSaveAsNew = () => {
    setEditingId(null)
  }

  const onLoadSchema = (schema) => {
    const nextCode = schema.code || ""
    setCode(nextCode)
    setSchemaName(schema.name || "")
    setEditingId(schema.id)
    if (editorRef.current) {
      editorRef.current.setValue(nextCode)
    }
  }

  const onDeleteSchema = (schema) => {
    const next = savedSchemas.filter((s) => s.id !== schema.id)
    saveSavedSchemas(next)
    setSavedSchemas(next)
    if (editingId === schema.id) {
      setEditingId(null)
    }
  }

  const onExportSchema = (schema) => {
    dl(JSON.stringify(schema, null, 2), `${schema.name || "schema"}.json`)
  }

  const handleDownload = () => {
    const text = editorRef.current?.getModel()?.getValue() || code || ""
    dl(text, "schema.pgs")
  }


  const handleAddNodeClick = () => {
    setElementModalMode("node")
    setEdgeContext(null)
    setElementModalOpen(true)
  }


  const handleAddEdgeClick = () => {
    if (graphEditMode === "add-edge") {
      setGraphEditMode(null)
      setEdgeSourceId(null)
    } else {
      setGraphEditMode("add-edge")
      setEdgeSourceId(null)
      setValidToast({ ok: true, msg: "Edge mode: click source node in the canvas" })
      setTimeout(() => setValidToast(null), 2000)
    }
  }

  const handleGraphNodeClick = (node) => {
    if (graphEditMode !== "add-edge") return

    if (!edgeSourceId) {
      setEdgeSourceId(node.id)
      setValidToast({ ok: true, msg: `Source selected: ${node.id}. Now click target node.` })
      setTimeout(() => setValidToast(null), 2000)
      return
    }

    const src = edgeSourceId
    const dst = node.id

    setGraphEditMode(null)
    setEdgeSourceId(null)
    setEdgeContext({ src, dst })
    setElementModalMode("edge")
    setElementModalOpen(true)
  }


  const handleModalSubmit = (data) => {
    if (data.mode === "node") {
      const nodeType = data.nodeType
      if (!nodeType) return

      const labels = normalizeLabels(data.nodeLabel)
      const labelsStr = labels.length > 0 ? labels.join(", ") : nodeType

      const propsArr = (data.props || [])
        .filter((p) => (p.name || "").trim())
        .map((p) => ({
          name: p.name.trim(),
          type: (p.type || "STRING").trim(),
          optional: !!p.optional,
        }))

      const propsStr = propsArr
        .map((p) => `${p.optional ? "OPTIONAL " : ""}${p.name} ${p.type}`)
        .join(", ")

      const propsBlock = propsStr ? ` { ${propsStr} }` : " { }"

      const snippet = `CREATE NODE TYPE (${nodeType} : ${labelsStr}${propsBlock});`
      appendSnippetToEditor(snippet)
      setValidToast({ ok: true, msg: "Node type added to schema." })
      setTimeout(() => setValidToast(null), 2000)
    } else if (data.mode === "edge") {
      const src = data.src
      const dst = data.dst
      if (!src || !dst) return

      const edgeTypeFinal = data.edgeType || `${src}_TO_${dst}`

      const labels = normalizeLabels(data.edgeLabel)
      const labelsStr = labels.length > 0 ? labels.join(", ") : edgeTypeFinal

      const propsArr = (data.props || [])
        .filter((p) => (p.name || "").trim())
        .map((p) => ({
          name: p.name.trim(),
          type: (p.type || "STRING").trim(),
          optional: !!p.optional,
        }))

      const propsStr = propsArr
        .map((p) => `${p.optional ? "OPTIONAL " : ""}${p.name} ${p.type}`)
        .join(", ")

      const propsBlock = propsStr ? ` { ${propsStr} }` : " { }"

      const snippet = `CREATE EDGE TYPE (${src}) - [ ${edgeTypeFinal} : ${labelsStr}${propsBlock} ] -> (${dst});`
      appendSnippetToEditor(snippet)
      setValidToast({ ok: true, msg: "Edge type added to schema." })
      setTimeout(() => setValidToast(null), 2000)
    }

    setElementModalOpen(false)
    setElementModalMode(null)
    setEdgeContext(null)
  }

  const handleModalClose = () => {
    setElementModalOpen(false)
    setElementModalMode(null)
    setEdgeContext(null)
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <ValidationToast validToast={validToast} />

      <GraphElementModal
        open={elementModalOpen}
        mode={elementModalMode}
        edgeContext={edgeContext}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-4rem)] overflow-hidden">
          <GraphPanel
            graphData={graphData}
            mode={graphEditMode}
            edgeSourceId={edgeSourceId}
            onAddNodeClick={handleAddNodeClick}
            onAddEdgeClick={handleAddEdgeClick}
            onNodeClick={handleGraphNodeClick}
          />

          <div className="lg:col-span-7 col-span-12 overflow-y-auto pr-1">
            <SchemaHeader />

            <SchemaToolbar
              schemaName={schemaName}
              setSchemaName={setSchemaName}
              editingId={editingId}
              onSaveSchema={onSaveSchema}
              onSaveAsNew={onSaveAsNew}
              onValidate={handleValidateClick}
              onDownload={handleDownload}
            />

            <SchemaEditor
              code={code}
              setCode={setCode}
              editorTheme={editorTheme}
              handleEditorWillMount={handleEditorWillMount}
              handleEditorDidMount={handleEditorDidMount}
            />

            <InfoSection />

            <SavedSchemasList
              savedSchemas={savedSchemas}
              editingId={editingId}
              onLoadSchema={onLoadSchema}
              onExportSchema={onExportSchema}
              onDeleteSchema={onDeleteSchema}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
